import React, { useEffect, useState } from "react";
import { Bell, Send, Save } from "lucide-react";
import { toast } from "sonner";
import { boardFetch } from "../api";
import { SectionHeader } from "../components";

const Settings = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    boardFetch("/reminders/settings").then(setForm).catch((e) => toast.error(e.message));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await boardFetch("/reminders/settings", {
        method: "PUT",
        json: { recipient: form.recipient, lead_days: Number(form.lead_days) || 7, enabled: form.enabled },
      });
      setForm(res);
      toast.success("Einstellungen gespeichert");
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await boardFetch("/reminders/send-now", { method: "POST" });
      if (res?.sent) toast.success(`Erinnerung an ${res.recipient} gesendet (${res.count} Punkt(e))`);
      else toast.info(res?.reason || "Nichts gesendet");
    } catch (e) { toast.error(e.message); } finally { setSending(false); }
  };

  if (!form) return <div className="py-20 text-center text-white/40 animate-pulse">Einstellungen werden geladen…</div>;

  return (
    <div data-testid="settings-page">
      <SectionHeader overline="Konfiguration" title="Einstellungen" />

      <div className="max-w-2xl border border-white/10 bg-white/[0.02] p-8">
        <div className="flex items-center gap-2 text-champagne">
          <Bell size={18} strokeWidth={1.6} />
          <h2 className="font-serif text-2xl font-light text-white">E-Mail-Erinnerungen</h2>
        </div>
        <p className="mt-2 text-sm text-white/45">
          Tägliche Zusammenfassung fälliger Audit-Feststellungen und Beschlüsse. Der Versand erfolgt automatisch um 07:00 Uhr (UTC),
          sofern aktiviert. Sie können jederzeit eine Test-Erinnerung auslösen.
        </p>

        <div className="mt-8 space-y-6">
          <div>
            <label className="mb-1.5 block text-xs uppercase tracking-wider text-white/50">Empfänger-Adresse</label>
            <input data-testid="settings-recipient" type="email" value={form.recipient || ""}
              onChange={(e) => setForm((s) => ({ ...s, recipient: e.target.value }))}
              placeholder="name@firma.ch"
              className="w-full border border-white/15 bg-black/30 px-3 py-2.5 text-sm text-white outline-none focus:border-champagne" />
          </div>

          <div>
            <label className="mb-1.5 block text-xs uppercase tracking-wider text-white/50">Vorlaufzeit (Tage)</label>
            <input data-testid="settings-lead-days" type="number" min="1" max="60" value={form.lead_days ?? 7}
              onChange={(e) => setForm((s) => ({ ...s, lead_days: e.target.value }))}
              className="w-32 border border-white/15 bg-black/30 px-3 py-2.5 text-sm text-white outline-none focus:border-champagne" />
            <p className="mt-1.5 text-xs text-white/35">Punkte, die innerhalb dieser Frist fällig werden, erscheinen in der Erinnerung.</p>
          </div>

          <label className="flex cursor-pointer items-center gap-3" data-testid="settings-enabled-label">
            <button type="button" role="switch" aria-checked={form.enabled} data-testid="settings-enabled"
              onClick={() => setForm((s) => ({ ...s, enabled: !s.enabled }))}
              className={`relative h-6 w-11 rounded-full transition-colors ${form.enabled ? "bg-champagne" : "bg-white/15"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${form.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
            <span className="text-sm text-white/75">Automatischer täglicher Versand {form.enabled ? "aktiv" : "deaktiviert"}</span>
          </label>
        </div>

        <div className="mt-8 flex flex-wrap gap-3 border-t border-white/10 pt-6">
          <button onClick={save} disabled={saving} data-testid="settings-save"
            className="inline-flex items-center gap-2 bg-champagne px-5 py-2.5 text-xs uppercase tracking-widest text-obsidian transition-colors hover:bg-champagne-light disabled:opacity-50">
            <Save size={15} /> {saving ? "Speichern…" : "Speichern"}
          </button>
          <button onClick={sendNow} disabled={sending || !form.recipient} data-testid="settings-send-now"
            className="inline-flex items-center gap-2 border border-champagne/40 px-5 py-2.5 text-xs uppercase tracking-widest text-champagne transition-colors hover:bg-champagne/10 disabled:opacity-40">
            <Send size={15} /> {sending ? "Sende…" : "Jetzt testen"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
